import React, { useState } from 'react';

import { useRouter } from 'next/router';

import { I18N_NAMESPACES } from 'i18n';
import { useTranslation } from 'i18n/hooks';
import { SpacedContainer } from 'revibe-ui';

import { Page404 } from 'modules/auth/pages/Page404';
import { Layout } from 'modules/common/components/Layout';
import { NoData } from 'modules/common/components/NoData';
import { ShopPage } from 'modules/common/components/ShopPage';
import { useInterval } from 'modules/common/hooks/useInterval';
import { convertObjectToQueryParams } from 'modules/common/utils';
import { ItemCollectionBanner } from 'modules/item/components/ItemCollectionBanner';
import { ItemsGridWithFilters } from 'modules/item/components/ItemsGridWithFilters';
import { ITEM_FILTERS } from 'modules/item/utils/itemFilter';
import { useFiltersQuery } from 'modules/catalog/hooks';

import { CloseIcon } from 'ui/icons/CloseIcon';
import { MarketplaceAPI } from 'revibe-api';
import { ITEM_STATUSES } from 'modules/item/utils/itemStatus';

type Props = { collection?: MarketplaceAPI.ItemCollection | null };

const Countdown = ({ releaseDate }: { releaseDate: string }) => {
  const countdownDate = new Date(releaseDate).getTime();
  const { tu } = useTranslation(I18N_NAMESPACES.COMMON);
  const getTime = () => {
    const currentTime = new Date().getTime();
    const distanceToDate = countdownDate - currentTime;

    const days = Math.floor(distanceToDate / (1000 * 60 * 60 * 24));
    let daysString = days.toString();
    const hours = Math.floor(
      (distanceToDate % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    let hoursString = hours.toString();
    const minutes = Math.floor(
      (distanceToDate % (1000 * 60 * 60)) / (1000 * 60)
    );
    let minutesString = minutes.toString();
    const seconds = Math.floor((distanceToDate % (1000 * 60)) / 1000);
    let secondsString = seconds.toString();

    const numbersToAddZeroTo = [1, 2, 3, 4, 5, 6, 7, 8, 9];

    daysString = `${days}`;
    if (numbersToAddZeroTo.includes(hours)) {
      hoursString = `0${hours}`;
    }
    if (numbersToAddZeroTo.includes(minutes)) {
      minutesString = `0${minutes}`;
    }
    if (numbersToAddZeroTo.includes(seconds)) {
      secondsString = `0${seconds}`;
    }

    return {
      days: daysString,
      hours: hoursString,
      minutes: minutesString,
      seconds: secondsString,
    };
  };
  const [state, setState] = useState(getTime());
  const setNewTime = () => {
    setState(getTime());
  };
  useInterval(setNewTime, 1000);

  return (
    <div className="grid grid-cols-4 gap-4">
      <SpacedContainer spacing="small" centered>
        <p className="text-2xl font-semibold">{state.days || '0'}</p>
        <p>{tu('days')}</p>
      </SpacedContainer>
      <SpacedContainer spacing="small" centered>
        <p className="text-2xl font-semibold">{state.hours || '00'}</p>
        <p>{tu('hours')}</p>
      </SpacedContainer>
      <SpacedContainer spacing="small" centered>
        <p className="text-2xl font-semibold">{state.minutes || '00'}</p>
        <p>{tu('minutes')}</p>
      </SpacedContainer>
      <SpacedContainer spacing="small" centered>
        <p className="text-2xl font-semibold">{state.seconds || '00'}</p>
        <p>{tu('seconds')}</p>
      </SpacedContainer>
    </div>
  );
};

export const ItemsCollectionPage = ({ collection = null }: Props) => {
  const { tu } = useTranslation(I18N_NAMESPACES.COLLECTION);
  const router = useRouter();
  const { filters, filtersToken, filtersQueryIsLoading } = useFiltersQuery();
  const basicFilters = {
    [ITEM_FILTERS.COLLECTION]: collection?.id,
    [ITEM_FILTERS.INCLUDE_UNRELEASED_COLLECTIONS]:
      router.query.sellerView === 'true',
    [ITEM_FILTERS.NOT_VERIFIED_ITEMS]: router.query.sellerView === 'true',
    [ITEM_FILTERS.STATUS]: [
      ITEM_STATUSES.APPROVED,
      router.query.sellerView === 'true' ? ITEM_STATUSES.PENDING_APPROVAL : '',
    ].join(','),
  };
  const handleFilterApply = (newFilters: MarketplaceAPI.ItemFilters) => {
    if (!collection) {
      return;
    }
    const url = `${router.asPath.split('?')[0]}?${convertObjectToQueryParams({
      sellerView: router.query.sellerView,
      ...newFilters,
      ...basicFilters,
    })}`;

    router.push(url);
  };

  if (!collection) {
    return <Page404 />;
  }

  if (!collection.is_published && router.query.sellerView !== 'true') {
    return (
      <ShopPage title={collection.name}>
        <Layout minHeight centered>
          <SpacedContainer centered>
            <NoData
              icon={<CloseIcon className="h-12 w-12" />}
              heading={tu('not-published-heading')}
              text={tu('not-published-text')}
            />
          </SpacedContainer>
        </Layout>
      </ShopPage>
    );
  }

  if (!collection.is_released && router.query.sellerView !== 'true') {
    return (
      <ShopPage title={collection.name}>
        <Layout minHeight centered>
          <SpacedContainer centered>
            <NoData
              icon={<CloseIcon className="h-12 w-12" />}
              heading={tu('not-released-heading')}
              text={tu('not-released-text', '', {
                date: collection.date,
              })}
            >
              <Countdown releaseDate={collection.rawDate} />
            </NoData>
          </SpacedContainer>
        </Layout>
      </ShopPage>
    );
  }

  return (
    <ShopPage title={collection.name}>
      <ItemCollectionBanner collection={collection} />
      {filtersQueryIsLoading ? null : (
        <ItemsGridWithFilters
          list={`${collection.name} by ${collection.seller.name}`}
          filters={{
            ...filters,
            ...basicFilters,
          }}
          onFiltersApplied={handleFilterApply}
          resetToken={filtersToken}
          pageSize={24}
        />
      )}
    </ShopPage>
  );
};
